/* Field utilities — formatting, labels, value rendering */

const TR_LABELS = {
    id: "ID",
    created_at: "Oluşturulma",
    updated_at: "Güncellenme",
    created: "Oluşturulma",
    modified: "Güncellenme",
    is_active: "Aktif",
    aktif: "Aktif",
    ad: "Ad",
    soyad: "Soyad",
    ad_soyad: "Ad Soyad",
    email: "E-posta",
    telefon: "Telefon",
    durum: "Durum",
    tarih: "Tarih",
    tutar: "Tutar",
    aciklama: "Açıklama",
    notlar: "Notlar",
    baslik: "Başlık",
    konu: "Konu",
    sira: "Sıra",
    dosya_no: "Dosya No",
    muvekkil: "Müvekkil",
};

export function humanizeKey(key) {
    if (!key) return "";
    if (TR_LABELS[key]) return TR_LABELS[key];
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

const BOOLEAN_FIELD_RE =
    /^(aktif|tamamlandi|odeme_yapildi|yatar_acilacak_mi|eksik_evrak_mail_gonderildi|sure_uyarisi_olusturuldu|gonderildi_mi|yatar_acilacak|mail_gonderildi)$/i;

export function isBooleanField(key, declaredType) {
    if (declaredType === "boolean") return true;
    return BOOLEAN_FIELD_RE.test(key || "");
}

export function coerceBoolean(value) {
    if (value === true || value === false) return value;
    if (value === 1 || value === "1" || value === "true" || value === "True")
        return true;
    if (value === 0 || value === "0" || value === "false" || value === "False")
        return false;
    return null;
}

export function detectType(key, value) {
    if (coerceBoolean(value) !== null || isBooleanField(key)) return "boolean";
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number") return "number";
    if (value && typeof value === "object") return "object";
    if (typeof value === "string") {
        // ISO datetime detection
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return "datetime";
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return "date";
        if (/^https?:\/\//i.test(value)) return "url";
        if (/^[\w.+-]+@[\w-]+\.[a-z]{2,}$/i.test(value)) return "email";
    }
    return "text";
}

export function formatValue(value, type, fieldKey) {
    if (value === null || value === undefined || value === "") {
        return <span className="text-slate-600">—</span>;
    }
    const coerced = coerceBoolean(value);
    const t =
        coerced !== null || isBooleanField(fieldKey, type)
            ? "boolean"
            : type || detectType(fieldKey, value);
    if (t === "boolean") {
        const b = coerced !== null ? coerced : !!value;
        return b ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Evet
            </span>
        ) : (
            <span className="inline-flex items-center gap-1.5 text-slate-500 text-xs">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
                Hayır
            </span>
        );
    }
    if (typeof value === "number" && isBooleanField(fieldKey, type)) {
        return value ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                Evet
            </span>
        ) : (
            <span className="inline-flex items-center gap-1.5 text-slate-500 text-xs">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-500" />
                Hayır
            </span>
        );
    }
    if (t === "datetime") {
        try {
            const d = new Date(value);
            return (
                <span className="font-mono-jb text-[12px]">
                    {d.toLocaleString("tr-TR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                    })}
                </span>
            );
        } catch {
            return String(value);
        }
    }
    if (t === "date") {
        try {
            const d = new Date(value);
            return (
                <span className="font-mono-jb text-[12px]">
                    {d.toLocaleDateString("tr-TR")}
                </span>
            );
        } catch {
            return String(value);
        }
    }
    if (t === "number") {
        return (
            <span className="font-mono-jb tabular-nums">
                {Number(value).toLocaleString("tr-TR")}
            </span>
        );
    }
    if (t === "url") {
        return (
            <a
                href={value}
                target="_blank"
                rel="noreferrer"
                className="text-[#F3E5AB] hover:underline truncate inline-block max-w-[260px]"
            >
                {value}
            </a>
        );
    }
    if (t === "object") {
        if (Array.isArray(value)) return `${value.length} öğe`;
        // Pretty-print nested object as label or id
        if (value?.name) return value.name;
        if (value?.title) return value.title;
        if (value?.ad) return value.ad;
        if (value?.id) return `#${value.id}`;
        return JSON.stringify(value).slice(0, 60);
    }
    if (t === "email") {
        return (
            <a
                href={`mailto:${value}`}
                className="text-[#F3E5AB] hover:underline"
            >
                {value}
            </a>
        );
    }
    const s = String(value);
    return s.length > 100 ? (
        <span title={s}>{s.slice(0, 100)}…</span>
    ) : (
        s
    );
}

export function statusTone(value) {
    if (!value) return "slate";
    const v = String(value).toLowerCase();
    // Yatar dosyası → critical crimson burgundy with pulse
    if (/yatar/.test(v)) return "yatar";
    if (/(tamam|onayl|aktif|completed|active|approved|done|success)/.test(v))
        return "green";
    if (/(beklem|pending|incelem|hazir|new|yeni|planland)/.test(v)) return "amber";
    if (/(iptal|red|cancel|hata|error|fail|kapal|pasif|inactive)/.test(v))
        return "red";
    if (/(devam|process|sürdür|sure)/.test(v)) return "blue";
    return "slate";
}

const TONE_CLASS = {
    green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    red: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    blue: "bg-sky-500/10 text-sky-400 border-sky-500/20",
    slate: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    yatar: "yatar-pulse",
};

export function StatusBadge({ value }) {
    if (!value) return <span className="text-slate-600">—</span>;
    const tone = statusTone(value);
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium uppercase tracking-wide ${TONE_CLASS[tone]}`}
        >
            <span
                className={`h-1.5 w-1.5 rounded-full ${
                    tone === "green"
                        ? "bg-emerald-400"
                        : tone === "amber"
                          ? "bg-amber-400"
                          : tone === "red"
                            ? "bg-rose-400"
                            : tone === "blue"
                              ? "bg-sky-400"
                              : tone === "yatar"
                                ? "bg-[#B14256]"
                                : "bg-slate-400"
                }`}
            />
            {String(value)}
        </span>
    );
}

export function isStatusKey(key) {
    return /^(durum|status|state|dosya_durumu|sure_durumu|onay_durumu|basvuru_turu|basvuru_konusu|vekalet_durumu|odeme_durumu)$/i.test(
        key,
    );
}
